import React, { useState, useCallback, useMemo } from "react"
import { useMutation, useApolloClient, ApolloQueryResult } from "@apollo/client"
import { useTokens } from "./useTokens"
import { WhoAmIResponse, WHO_AM_I } from "../../queries/WHO_AM_I"
import { Branding } from "../../../typings/types"
import { useHistory } from "react-router"
import { REQUEST_LOGIN } from "../../mutations/REQUEST_LOGIN"

export interface CurrentUser {
    id?: number
    email?: string
    isAccountLocked?: boolean
    failedLoginCount?: number
}

export interface AuthContextValue {
    currentUser: CurrentUser | undefined
    branding: Branding | undefined
    authenticated: boolean | undefined
    requestLogin: (email: string, recaptcha: string) => Promise<void>
    logout: () => void
    loadCurrentUser: () => void
    loadingCurrentUser: boolean
}

export const AuthContext = React.createContext<AuthContextValue>({
    requestLogin: (email: string, recaptcha: string) => {
        return Promise.resolve()
    },
    logout: () => undefined,
    loadCurrentUser: () => {
        return Promise.resolve()
    },
    authenticated: undefined,
    loadingCurrentUser: false,
    currentUser: undefined,
    branding: undefined
})

interface AuthProviderProps {
    value?: AuthContextValue
    children: React.ReactNode
}

export const PUBLIC_PATHS = ["/login", "/verify", "/contact"]
export const isPublicPath = (path: string) => PUBLIC_PATHS.some(p => path.match(p))

export const AuthProvider: React.FunctionComponent<AuthProviderProps> = props => {
    const { setAccessToken, setRefreshToken, getAccessToken } = useTokens()
    const [authenticated, setAuthenticated] = useState<boolean | undefined>(undefined)
    const [currentUser, setCurrentUser] = useState<CurrentUser | undefined>(undefined)
    const [branding, setBranding] = useState<Branding | undefined>(undefined)
    const [loadingCurrentUser, setLoadingCurrentUser] = useState<boolean>(false)
    const client = useApolloClient()
    const history = useHistory()

    const [invokeRequestLogin] = useMutation(REQUEST_LOGIN, {
        onError: e => {
            throw e
        }
    })

    const loadCurrentUser = useCallback(() => {
        setLoadingCurrentUser(true)
        client
            .query<WhoAmIResponse>({ query: WHO_AM_I, fetchPolicy: "network-only" })
            .then((result: ApolloQueryResult<WhoAmIResponse>) => {
                setCurrentUser({
                    email: result?.data?.whoAmI.email
                })
                setBranding(result?.data?.whoAmI?.company.branding)
                setAuthenticated(true)
            })
            .catch(() => {
                setAuthenticated(false)
                history.push("/login")
            })
            .finally(() => {
                setLoadingCurrentUser(false)
            })
    }, [setLoadingCurrentUser, setAuthenticated, setCurrentUser, history])

    const requestLogin = useCallback(
        async (email: string, recaptcha: string) => {
            return new Promise<void>((resolve, reject) => {
                invokeRequestLogin({
                    variables: {
                        email: email
                    },
                    context: { headers: { recaptcha } }
                })
                    .then(() => {
                        resolve()
                    })
                    .catch(e => {
                        reject(e)
                    })
            })
        },
        [invokeRequestLogin]
    )

    const logout = useCallback(() => {
        setAccessToken("")
        setRefreshToken("")
        setAuthenticated(false)
        setCurrentUser(undefined)
        setBranding(undefined)
    }, [setAccessToken, setRefreshToken])

    const authContextValue = useMemo(
        () => ({
            currentUser,
            branding,
            authenticated,
            requestLogin,
            logout,
            loadCurrentUser,
            loadingCurrentUser
        }),
        [
            requestLogin,
            branding,
            authenticated,
            currentUser,
            logout,
            loadCurrentUser,
            loadingCurrentUser
        ]
    )

    return <AuthContext.Provider value={props.value ?? authContextValue} {...props} />
}

export const useAuth = (): AuthContextValue => {
    const context = React.useContext(AuthContext)
    if (!context) throw new Error("useAuth must be used within an AuthProvider")
    return context
}
